import { useState, useEffect } from "react";
import { toast } from "sonner";
import { ethers } from "ethers";

export type EthereumWalletState = {
  isConnected: boolean;
  address: string | null;
  chainId: string | null;
  isConnecting: boolean;
  balance: string | null;
  provider: any | null;
  signer: any | null;
  error: string | null;
};

// Define proper Ethereum interface to avoid type conflicts
interface EthereumProvider {
  isMetaMask?: boolean;
  request: (args: { method: string; params?: any[] }) => Promise<any>;
  on: (event: string, callback: (...args: any[]) => void) => void;
  removeAllListeners: (event: string) => void;
}

export const useEthereumWallet = () => {
  const [walletState, setWalletState] = useState<EthereumWalletState>({
    isConnected: false,
    address: null,
    chainId: null,
    isConnecting: false,
    balance: null,
    provider: null,
    signer: null,
    error: null
  });

  // Check if MetaMask is installed and initialize
  useEffect(() => {
    const checkForMetaMask = async () => {
      // Reset any previous errors
      setWalletState(prev => ({ ...prev, error: null }));
      
      if (typeof window !== 'undefined' && typeof window.ethereum !== 'undefined') {
        try {
          // Setup provider
          const provider = new ethers.providers.Web3Provider(window.ethereum);
          
          // Check if already connected
          const accounts = await provider.listAccounts();
          
          if (accounts.length > 0) {
            const signer = provider.getSigner();
            const address = await signer.getAddress();
            const network = await provider.getNetwork();
            const balanceWei = await provider.getBalance(address);
            const balance = ethers.utils.formatEther(balanceWei);
            
            setWalletState({
              isConnected: true,
              address,
              chainId: network.chainId.toString(),
              isConnecting: false,
              balance,
              provider,
              signer,
              error: null
            });
          } else {
            setWalletState(prev => ({ 
              ...prev, 
              provider, 
              isConnected: false 
            }));
          }
        } catch (err: any) {
          console.error("Error initializing wallet:", err);
          setWalletState(prev => ({ 
            ...prev, 
            error: err.message || "Failed to initialize wallet connection" 
          }));
        }
      } else {
        setWalletState(prev => ({ 
          ...prev, 
          error: "MetaMask not installed" 
        }));
      }
    };

    checkForMetaMask();
    
    // Setup event listeners
    if (typeof window !== 'undefined' && typeof window.ethereum !== 'undefined') {
      // Use the correct typings for ethereum
      const ethereum = window.ethereum as any;
      
      ethereum.on('accountsChanged', (accounts: string[]) => {
        if (accounts.length === 0) {
          // User disconnected
          setWalletState({
            isConnected: false,
            address: null,
            chainId: null,
            isConnecting: false,
            balance: null,
            provider: null,
            signer: null,
            error: null
          });
        } else {
          // Account changed, update state
          checkForMetaMask();
        }
      });
      
      ethereum.on('chainChanged', () => {
        // Chain changed, refresh provider
        checkForMetaMask();
      });
      
      // Cleanup event listeners when component unmounts
      return () => {
        try {
          ethereum.removeAllListeners('accountsChanged');
          ethereum.removeAllListeners('chainChanged');
        } catch (error) {
          console.error("Error removing event listeners:", error);
        }
      };
    }
    
    return undefined;
  }, []);
  
  // Function to connect wallet
  const connectWallet = async () => {
    setWalletState(prev => ({ ...prev, isConnecting: true, error: null }));
    
    try {
      if (typeof window.ethereum === 'undefined') {
        throw new Error("MetaMask is not installed");
      }
      
      // Request account access
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      await provider.send("eth_requestAccounts", []);
      
      const signer = provider.getSigner();
      const address = await signer.getAddress();
      const network = await provider.getNetwork();
      const balanceWei = await provider.getBalance(address);
      const balance = ethers.utils.formatEther(balanceWei);
      
      setWalletState({
        isConnected: true,
        address,
        chainId: network.chainId.toString(),
        isConnecting: false,
        balance,
        provider,
        signer,
        error: null
      });
      
      toast.success("Carteira conectada com sucesso");
    } catch (err: any) {
      console.error("Error connecting wallet:", err);
      setWalletState(prev => ({ 
        ...prev, 
        isConnecting: false, 
        error: err.message || "Failed to connect wallet" 
      }));
      toast.error(err.message || "Falha ao conectar carteira");
    }
  };
  
  // Function to disconnect wallet
  const disconnectWallet = () => {
    setWalletState({
      isConnected: false,
      address: null,
      chainId: null,
      isConnecting: false,
      balance: null,
      provider: null,
      signer: null,
      error: null
    });
    toast.info("Carteira desconectada");
  };
  
  // Handle specific blockchain interactions like switching networks
  const switchToNetwork = async (chainId: string) => {
    if (typeof window.ethereum === 'undefined') return false;
    
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId }],
      });
      return true;
    } catch (error: any) {
      // This error code indicates the chain has not been added to MetaMask
      if (error.code === 4902) {
        // You could implement adding the chain here if needed
        toast.error("Rede não disponível na sua carteira");
      } else {
        toast.error("Erro ao mudar de rede");
      }
      return false;
    }
  };
  
  return {
    ...walletState,
    connectWallet,
    disconnectWallet,
    switchToNetwork
  };
};

// Fixed type definition for window.ethereum - making it consistent with the EthereumProvider interface
declare global {
  interface Window {
    ethereum?: EthereumProvider;
  }
}
